import type { EducationClass } from 'shared/api';
import type {
  ScheduleConstraint,
  ScheduleConstraintCategory,
  ScheduleConstraintCode,
} from '../schedule-configuration';
import type { DifficultyDistributionModelCode } from '../schedule-configuration/types';

export type EducationClassWithCurriculum = {
  id: number;
  educationClassId: number;
  curriculumId: number | null;
  litera: string;
  parallel: string;
};

export type CurriculumSource = 'INTEGRATION' | 'USER_CREATED';

export type CurriculumItem = {
  id: number;
  name: string;
  source?: CurriculumSource;
};

export type Curriculum = {
  id?: number;
  name: string;
  source?: CurriculumSource;
  items?: CurriculumElement[];
};

export type CurriculumElement = {
  id?: number;
  subjectId: number;
  subjectAreaId: number;
  litera: string;
  parallel: string;
  hours: number;
};

export type CurriculumSyncInfoResponse = {
  count: number;
  lastSyncDate: string;
  curriculums: Curriculum[];
};

export type LastSyncInfo = {
  count: number;
  lastSyncDate: string;
};

export type UpdateEducationClassCurriculumBody = {
  curriculumId: number;
};

export type EducationPlanSchedule = {
  id: number;
  date: string;
};

export type DayOfWeek =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export type Lesson =
  | 'FIRST'
  | 'SECOND'
  | 'THIRD'
  | 'FOURTH'
  | 'FIFTH'
  | 'SIXTH'
  | 'SEVENTH'
  | 'EIGHTH';

export type Teacher = {
  id: number;
  code: string;
};

export type ScheduleBuilding = {
  id: number;
  name: string;
  address: string;
};

export type ScheduleRoom = {
  id: number;
  name: string;
  number: string | number;
  capacity: number;
  building: ScheduleBuilding;
};

export type ScheduleSubject = {
  id: number;
  name: string;
};

export type ScheduleGroup = {
  id: number;
  name: string;
};

export type ScheduleLesson = {
  id: number;
  teacher: Teacher;
  room: ScheduleRoom;
  subject: ScheduleSubject;
  pinned?: boolean;
  group?: ScheduleGroup;
};

export type Timeslot = {
  id: number;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  pinned: boolean;
  lessons: ScheduleLesson[];
};

export type Score = {
  perfect: boolean;
};

export type GetScheduleResponse = {
  items: Schedule[];
  score: Score;
  constraints: ScheduleConstraint[];
};

export interface Schedule {
  id: number;
  educationalClass: EducationClass;
  timeslots: Timeslot[];
}

export enum CurriculumScheduleStatus {
  Error = 'ERROR',
  None = 'NONE',
  Processing = 'PROCESSING',
}

export type ScheduleErrorTimeslot = {
  educationalClass: EducationClass;
  dayOfWeek: DayOfWeek;
  startTime: string;
  endTime: string;
  category?: ScheduleConstraintCategory;
  code?: ScheduleConstraintCode;
  lessonId: number;
  pinned: boolean;
  subject: ScheduleSubject;
  teacher: Teacher;
  room: ScheduleRoom;
};

export enum ScheduleErrorType {
  Hard = 'HARD',
  Soft = 'SOFT',
}

export type ScheduleErrorTimeslotGroup = {
  id: number;
  timeslots: ScheduleErrorTimeslot[];
};

export type ScheduleViolation = {
  id: number;
  code: ScheduleConstraintCode;
  type: ScheduleErrorType;
  timeslotGroups: ScheduleErrorTimeslotGroup[];
  category: ScheduleConstraintCategory;
};

export type DailyViolation = {
  actualDifficultyPoints: number;
  actualPercent: number;
  dayOfWeek: DayOfWeek;
  deviationPercent: number;
  expectedMaxPercent: number;
  expectedMinPercent: number;
  penaltyScore: number;
};

export type CurriculumReport = {
  dailyViolations: DailyViolation[];
  educationalClass: EducationClass;
  totalPenalty: number;
  weeklyTotalPoints: number;
};

export type DifficultyDistribution = {
  curriculumReports: CurriculumReport[];
  totalPenalty: number;
  totalViolationsCount: number;
  difficultyDistributionModelCode: DifficultyDistributionModelCode | null;
};

export enum ScheduleReportStatus {
  CRITICAL_WITH_WARNINGS = 'CRITICAL_WITH_WARNINGS',
  CRITICAL = 'CRITICAL',
  WARNING = 'WARNING',
  SUCCESS = 'SUCCESS',
}

export type ScheduleReport = {
  status: ScheduleReportStatus;
  constraints: ScheduleConstraint[];
  violations: ScheduleViolation[];
  difficultyDistribution: DifficultyDistribution;
};
